<template>
  <Teleport to="body">
    <div class="fixed inset-0 z-[10050] bg-black text-white">
      <video
        v-show="!frozenUrl"
        ref="videoRef"
        class="absolute inset-0 h-full w-full object-cover"
        autoplay
        playsinline
        muted
      />
      <PinchZoomImage
        v-if="frozenUrl"
        :src="frozenUrl"
        :one-finger-draws="isMarking"
        @draw-start="handleDrawStart"
        @draw-move="handleDrawMove"
        @draw-cancel="handleDrawCancel"
      >
        <DamageMarkingOverlay
          v-if="frozenSize"
          :strokes="strokes"
          :width="frozenSize.width"
          :height="frozenSize.height"
        />
      </PinchZoomImage>
      <div
        v-if="frozenUrl && frozenSize"
        class="absolute flex flex-col-reverse gap-3"
        :style="penToolsStyle"
      >
        <button
          type="button"
          :class="cameraButton({ variant: isMarking ? 'sideActive' : 'side' })"
          :aria-label="t('mark')"
          :aria-pressed="isMarking"
          @click="handleToggleMarking"
        >
          <PencilIcon class="size-6" />
        </button>
        <template v-if="isMarking">
          <button
            v-for="color in markingColorOptions"
            :key="color"
            type="button"
            :class="cameraButton()"
            :aria-label="t(`colors.${color}`)"
            :aria-pressed="markingColor === color"
            @click="markingColor = color"
          >
            <span
              class="block size-6 rounded-full"
              :class="{
                'ring-2 ring-white ring-offset-2 ring-offset-black': markingColor === color,
              }"
              :style="{ backgroundColor: MARKING_COLORS[color] }"
            />
          </button>
          <button
            type="button"
            :class="cameraButton()"
            :aria-label="t('undo')"
            :disabled="!strokes.length"
            @click="handleUndoStroke"
          >
            <Undo2Icon class="size-6" />
          </button>
        </template>
      </div>
      <p
        v-if="isFrozenBlurred"
        class="pointer-events-none absolute left-4 top-[max(1rem,calc(env(safe-area-inset-top)+0.5rem))] flex items-center justify-center gap-2 rounded-md bg-black/70 px-4 py-2 text-center text-sm"
        :style="{ right: `${(isLandscape ? railWidth : 0) + RAIL_GAP_PX}px` }"
      >
        <AlertTriangleIcon class="size-4 shrink-0 text-hazel-300" />
        {{ t('blurHint') }}
      </p>
      <div ref="railRef" :class="railClass">
        <div :class="controlsClass">
          <button
            type="button"
            class="justify-self-start"
            :class="cameraButton()"
            :aria-label="t('cancel')"
            :disabled="isConfirming"
            @click="handleClose"
          >
            <XIcon class="size-6" />
          </button>
          <button
            v-if="!frozenUrl"
            type="button"
            class="justify-self-center"
            :class="cameraButton({ variant: 'shutter' })"
            :aria-label="t('takePhoto')"
            @click="handleShutter"
          >
            <span class="absolute inset-[6px] rounded-full bg-white" />
          </button>
          <button
            v-else
            type="button"
            class="justify-self-center"
            :class="cameraButton({ variant: 'confirm' })"
            :aria-label="t('confirm')"
            :disabled="isConfirming"
            @click="handleConfirm"
          >
            <CheckIcon class="size-[2.5rem]" />
          </button>
          <button
            v-if="!frozenUrl && hasTorch"
            type="button"
            class="justify-self-end"
            :class="cameraButton()"
            :aria-label="t('flash')"
            @click="handleToggleTorch"
          >
            <ZapIcon class="size-6" :class="{ 'text-yellow-400': isTorchOn }" />
          </button>
          <button
            v-else-if="frozenUrl"
            type="button"
            class="justify-self-end"
            :class="cameraButton()"
            :aria-label="t('retake')"
            :disabled="isConfirming"
            @click="handleRetake"
          >
            <RotateCcwIcon class="size-6" />
          </button>
          <span v-else class="block size-[3.25rem] justify-self-end" />
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, onUnmounted, ref } from 'vue';
import { useElementSize } from '@vueuse/core';
import { cameraButton } from '@carvia/theme/lib/cva/camera-button';
import { useI18n } from 'vue-i18n';
import {
  AlertTriangleIcon,
  CheckIcon,
  PencilIcon,
  RotateCcwIcon,
  Undo2Icon,
  XIcon,
  ZapIcon,
} from 'lucide-vue-next';
import PinchZoomImage, { type FramePoint } from '@/components/PinchZoomImage.vue';
import { useCameraStream } from '@/hooks/use-camera-stream';
import { useCameraLayout } from '@/hooks/use-camera-layout';
import { captureSharpestVideoFrame, PHOTO_STREAM_SIZE } from '@/utils/camera-capture';
import { SHARPNESS_THRESHOLD } from '@/utils/sharpness';
import {
  burnMarkingIntoPhoto,
  MARKING_COLORS,
  toPhotoPoint,
  type MarkingColor,
  type MarkingStroke,
} from '@/utils/damage-marking';
import DamageMarkingOverlay from './components/DamageMarkingOverlay.vue';

const emit = defineEmits<{
  (e: 'captured', file: File): void;
  (e: 'close'): void;
  (e: 'failed'): void;
}>();

const { t } = useI18n();
const { isLandscape, orientationAngle, railClass, controlsClass } = useCameraLayout();
const railRef = ref<HTMLElement | null>(null);
const { width: railWidth, height: railHeight } = useElementSize(railRef, undefined, {
  box: 'border-box',
});

/** The rail's own padding, so controls beside it line up with the buttons inside it. */
const railPadding = computed(() => {
  // Re-read when the rail resizes or turns; padding comes from its orientation classes.
  void railWidth.value;
  void railHeight.value;
  void isLandscape.value;
  const style = railRef.value ? getComputedStyle(railRef.value) : null;
  return {
    right: parseFloat(style?.paddingRight ?? '0'),
    bottom: parseFloat(style?.paddingBottom ?? '0'),
  };
});

/** Gap between the rail and the pen tools beside it. */
const RAIL_GAP_PX = 16;

/**
 * Places the pen tools beside the rail, on the same edge as the rail's own buttons: above it at
 * the right in portrait, left of it at the bottom in landscape.
 */
const penToolsStyle = computed(() =>
  isLandscape.value
    ? { right: `${railWidth.value + RAIL_GAP_PX}px`, bottom: `${railPadding.value.bottom}px` }
    : { right: `${railPadding.value.right}px`, bottom: `${railHeight.value + RAIL_GAP_PX}px` },
);
const videoRef = ref<HTMLVideoElement | null>(null);
const {
  hasTorch,
  isTorchOn,
  toggleTorch: handleToggleTorch,
} = useCameraStream(
  videoRef,
  { video: { facingMode: { ideal: 'environment' }, ...PHOTO_STREAM_SIZE } },
  () => emit('failed'),
);
const frozenUrl = ref('');
const frozenFile = ref<File | null>(null);
const frozenSharpness = ref(Infinity);
const isFrozenBlurred = computed(
  () => !!frozenUrl.value && frozenSharpness.value < SHARPNESS_THRESHOLD,
);

const frozenSize = ref<{ width: number; height: number } | null>(null);
const markingColorOptions: MarkingColor[] = ['red', 'green'];
const isMarking = ref(false);
const markingColor = ref<MarkingColor>('red');
const strokes = ref<MarkingStroke[]>([]);
const isConfirming = ref(false);

const handleShutter = async () => {
  if (!videoRef.value) return;
  const frame = await captureSharpestVideoFrame(videoRef.value, orientationAngle.value);
  if (!frame) return;
  const bitmap = await createImageBitmap(frame.file);
  frozenSize.value = { width: bitmap.width, height: bitmap.height };
  bitmap.close();
  frozenFile.value = frame.file;
  frozenSharpness.value = frame.sharpness;
  if (frozenUrl.value) URL.revokeObjectURL(frozenUrl.value);
  frozenUrl.value = URL.createObjectURL(frozenFile.value);
};

/** Drawing keeps the current zoom; one finger draws, two fingers keep zooming and panning. */
const handleToggleMarking = () => {
  isMarking.value = !isMarking.value;
};

const getPhotoPoint = ({ x, y, frameWidth, frameHeight }: FramePoint) =>
  toPhotoPoint({ x, y }, { width: frameWidth, height: frameHeight }, frozenSize.value!);

const handleDrawStart = (point: FramePoint) => {
  if (!frozenSize.value) return;
  strokes.value.push({ color: markingColor.value, points: [getPhotoPoint(point)] });
};

const handleDrawMove = (point: FramePoint) => {
  if (!frozenSize.value) return;
  strokes.value[strokes.value.length - 1]?.points.push(getPhotoPoint(point));
};

const handleDrawCancel = () => {
  strokes.value.pop();
};

const handleUndoStroke = () => {
  strokes.value = strokes.value.slice(0, -1);
};

const handleRetake = () => {
  frozenFile.value = null;
  frozenSize.value = null;
  strokes.value = [];
  isMarking.value = false;
  if (frozenUrl.value) URL.revokeObjectURL(frozenUrl.value);
  frozenUrl.value = '';
};

const handleConfirm = async () => {
  if (!frozenFile.value || isConfirming.value) return;
  const photo = frozenFile.value;
  isConfirming.value = true;
  try {
    const marked = await burnMarkingIntoPhoto(photo, strokes.value);
    // Retake and close are disabled meanwhile; still, only the photo on screen may be kept.
    if (frozenFile.value === photo) emit('captured', marked);
  } finally {
    isConfirming.value = false;
  }
};

const handleClose = () => {
  emit('close');
};

onUnmounted(() => {
  if (frozenUrl.value) URL.revokeObjectURL(frozenUrl.value);
});
</script>

<i18n lang="json">
{
  "en": {
    "takePhoto": "Take photo",
    "blurHint": "Photo looks blurry. Zoom in to check or take it again.",
    "confirm": "Confirm",
    "retake": "Retake",
    "flash": "Flash",
    "cancel": "Cancel",
    "mark": "Mark damage",
    "undo": "Undo",
    "colors": { "red": "Red pen", "green": "Green pen" }
  },
  "de": {
    "takePhoto": "Foto aufnehmen",
    "blurHint": "Foto wirkt unscharf. Zum Prüfen heranzoomen oder neu aufnehmen.",
    "confirm": "Bestätigen",
    "retake": "Erneut",
    "flash": "Blitz",
    "cancel": "Abbrechen",
    "mark": "Schaden markieren",
    "undo": "Rückgängig",
    "colors": { "red": "Roter Stift", "green": "Grüner Stift" }
  },
  "es": {
    "takePhoto": "Hacer foto",
    "blurHint": "La foto parece borrosa. Amplía para comprobarla o repítela.",
    "confirm": "Confirmar",
    "retake": "Repetir",
    "flash": "Flash",
    "cancel": "Cancelar",
    "mark": "Marcar daño",
    "undo": "Deshacer",
    "colors": { "red": "Lápiz rojo", "green": "Lápiz verde" }
  }
}
</i18n>
